import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase F.6 Batch D+ — Magic-link landing page.
 *
 * Flow:
 *   1. Email recipient clicks "View & Sign" CTA in their inbox
 *   2. They land here at /portal/sign?token=<JWT>
 *   3. We POST /api/auth/magic-login with the token, get an access_token + doc_id
 *   4. Set auth context, route to the right portal home with ?focus=doc_id
 *
 * Failures (expired / invalid / deactivated account) render a friendly error.
 */
const PortalSignLanding = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [status, setStatus] = useState("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Missing sign-in token. Please use the link from your email.");
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/magic-login`, { token });
        const { access_token, user, doc_id } = res.data;
        setAuth(access_token, user);
        setStatus("success");
        // Route by account_type → portal home + focus doc query param
        const target =
          user.account_type === "client_portal"
            ? `/client-portal?focus=${doc_id}`
            : user.account_type === "employee_portal"
            ? `/employee-portal?focus=${doc_id}`
            : `/dashboard`;
        setTimeout(() => navigate(target, { replace: true }), 600);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setStatus("error");
        setErrorMsg(
          typeof detail === "string"
            ? detail
            : "We couldn't sign you in with that link. It may have expired."
        );
      }
    })();
  }, [params, navigate, setAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="magic-link-landing">
      <div className="max-w-md w-full bg-card border rounded-xl p-8 shadow-lg text-center space-y-4">
        {status === "loading" && (
          <>
            <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" data-testid="magic-loading" />
            <h1 className="text-xl font-semibold">Signing you in…</h1>
            <p className="text-sm text-muted-foreground">Verifying your secure link.</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto" data-testid="magic-success" />
            <h1 className="text-xl font-semibold">Welcome back!</h1>
            <p className="text-sm text-muted-foreground">Taking you straight to your document…</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto" data-testid="magic-error" />
            <h1 className="text-xl font-semibold">Sign-in link issue</h1>
            <p className="text-sm text-muted-foreground" data-testid="magic-error-msg">{errorMsg}</p>
            <Button onClick={() => navigate("/login")} className="mt-2" data-testid="magic-go-login">
              Sign in with your password
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default PortalSignLanding;
